import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useDiet } from "@/contexts/DietContext";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Plus, Clock, Trash2, Lock, ChevronLeft, ChevronRight, Award, Timer, TrendingDown, UtensilsCrossed, Check, PlusCircle, Loader2, Droplets } from "lucide-react";
import WaterTracker from "@/components/diary/WaterTracker";
import RecipeEmoji from "@/components/RecipeEmoji";
import { recipes, diaryMealToRecipeKey, findRecipeById, parseScaledRecipeId, Recipe, type MealKey } from "@/data/recipes";
import { useSavedRecipes, getRecipeTotals, type SavedRecipe } from "@/hooks/useSavedRecipes";
import { apiFetch } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import DiarySkeleton from "@/components/diary/DiarySkeleton";
import { AchievementsSheet, TOTAL_ACHIEVEMENTS, achievements } from "@/components/diary/AchievementsSheet";
import type { AchievementContext } from "@/components/diary/AchievementsSheet";

interface DiaryEntry {
  id: string;
  meal_type: string;
  description: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  time: string;
  photo_path?: string;
  photo_url?: string;
  date: string;
  from_diet?: boolean;
}

const mealTypeLabels: Record<string, { label: string; emoji: string }> = {
  breakfast: { label: "Café da Manhã", emoji: "🌅" },
  morning_snack: { label: "Lanche da Manhã", emoji: "🥤" },
  lunch: { label: "Almoço", emoji: "🍽️" },
  afternoon_snack: { label: "Lanche da Tarde", emoji: "🍪" },
  dinner: { label: "Jantar", emoji: "🌙" },
  optional_snack: { label: "Lanche Opcional", emoji: "🥜" },
  other: { label: "Outro", emoji: "🍴" },
};

const Diary = () => {
  const { selectedPlan, measures, meals, planLoading, savedDiets, dietPlan } = useDiet();
  const { recipes: userRecipes, globalRecipes } = useSavedRecipes();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [entries, setEntries] = useState<DiaryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [formMode, setFormMode] = useState<"diet" | "custom">("diet");
  const [selectedRecipes, setSelectedRecipes] = useState<string[]>([]);
  const [selectedDietOption, setSelectedDietOption] = useState<number>(0);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>("");
  const [formData, setFormData] = useState({
    mealType: "breakfast",
    description: "",
    calories: "",
    protein: "",
    carbs: "",
    fat: "",
    time: new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
  });

  const dateToString = (d: Date) => d.toISOString().split("T")[0];
  const selectedDateStr = dateToString(selectedDate);
  const todayStr = dateToString(new Date());

  // Find the diet that was active on the selected date
  const dietForDate = useMemo(() => {
    if (!savedDiets || savedDiets.length === 0) return null;
    const sorted = [...savedDiets].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
    let activeDiet = sorted[0];
    for (const diet of sorted) {
      const dietDate = diet.created_at.split("T")[0];
      if (dietDate <= selectedDateStr) {
        activeDiet = diet;
      } else {
        break;
      }
    }
    return activeDiet;
  }, [savedDiets, selectedDateStr]);

  const targetCalories = dietForDate?.measures?.calories || measures?.calories || 2000;
  const mealsForDate = dietForDate?.meals || meals;

  // Calendar logic
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const daysInMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1).getDay();
  const monthName = calendarMonth.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  const weekDays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];


  // Load entries from backend
  const loadEntries = useCallback(async () => {
    if (!user) return;
    try {
      const res = await apiFetch("api-diary", { action: "list" });
      setEntries(res.data || []);
    } catch (e) {
      console.error("Failed to load diary entries:", e);
      toast({ title: "Erro ao carregar diário", description: "Tente novamente mais tarde", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  useEffect(() => {
    if (user && selectedPlan && selectedPlan !== "basic" && selectedPlan !== "free") {
      loadEntries();
    } else {
      setLoading(false);
    }
  }, [user, selectedPlan, loadEntries]);

  const datesWithEntries = useMemo(() => {
    const set = new Set<string>();
    entries.forEach(e => set.add(e.date));
    return set;
  }, [entries]);

  // Calculate which dates have met the calorie target (80-110%)
  const datesOnTarget = useMemo(() => {
    const caloriesByDay: Record<string, number> = {};
    entries.forEach(e => {
      caloriesByDay[e.date] = (caloriesByDay[e.date] || 0) + Number(e.calories);
    });

    const set = new Set<string>();
    const sortedDiets = savedDiets
      ? [...savedDiets].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
      : [];

    for (const [day, cal] of Object.entries(caloriesByDay)) {
      let target = measures?.calories || 2000;
      for (const diet of sortedDiets) {
        const dietDate = diet.created_at.split("T")[0];
        if (dietDate <= day) {
          const m = diet.measures as any;
          if (m?.calories) target = Number(m.calories);
        } else break;
      }
      const ratio = cal / target;
      if (ratio >= 0.8 && ratio <= 1.1) set.add(day);
    }
    return set;
  }, [entries, savedDiets, measures]);

  const dayEntries = useMemo(() => entries.filter(e => e.date === selectedDateStr), [entries, selectedDateStr]);
  const dayCalories = Math.round(dayEntries.reduce((sum, e) => sum + Number(e.calories), 0));
  const dayProtein = Math.round(dayEntries.reduce((sum, e) => sum + Number(e.protein), 0));
  const dayCarbs = Math.round(dayEntries.reduce((sum, e) => sum + Number(e.carbs), 0));
  const dayFat = Math.round(dayEntries.reduce((sum, e) => sum + Number(e.fat), 0));
  const remainingCalories = targetCalories - dayCalories;
  const caloriePercent = Math.min(Math.round((dayCalories / targetCalories) * 100), 100);

  const streak = useMemo(() => {
    let count = 0;
    const d = new Date();
    while (true) {
      const ds = dateToString(d);
      if (datesWithEntries.has(ds)) {
        count++;
        d.setDate(d.getDate() - 1);
      } else break;
    }
    return count;
  }, [datesWithEntries]);

  const [achievementsContext, setAchievementsContext] = useState<AchievementContext | null>(null);

  const loadAchievementsContext = useCallback(async () => {
    if (!user) return;
    try {
      const res = await apiFetch("api-diary", { action: "achievements" });
      const d = res.data;
      setAchievementsContext({
        totalEntries: d.totalEntries,
        streak: d.streak,
        uniqueDays: d.uniqueDays,
        daysOnTarget: d.daysOnTarget,
        consecutiveDaysOnTarget: d.consecutiveDaysOnTarget || 0,
        mealTypesUsed: new Set(d.mealTypesUsed),
        hasPhoto: d.hasPhoto,
        entriesWithPhoto: d.entriesWithPhoto,
        totalMetricEntries: d.totalMetricEntries || 0,
        metricsWithPhotos: d.metricsWithPhotos || 0,
        measuredBodyParts: d.measuredBodyParts || 0,
        weightVariation: d.weightVariation || false,
        totalWaterDays: d.totalWaterDays || 0,
        waterOnTargetDays: d.waterOnTargetDays || 0,
        consecutiveWaterDays: d.consecutiveWaterDays || 0,
        waterStreak: d.waterStreak || 0,
      });
    } catch (e) {
      console.error("Failed to load achievements context:", e);
    }
  }, [user]);

  useEffect(() => {
    loadAchievementsContext();
  }, [loadAchievementsContext]);

  const unlockedAchievements = useMemo(() => {
    if (!achievementsContext) return 0;
    return achievements.filter(a => a.check(achievementsContext)).length;
  }, [achievementsContext]);

  const allSavedRecipes = useMemo(() => [...userRecipes, ...globalRecipes], [userRecipes, globalRecipes]);

  /** Resolve any recipe ID (static, saved_, scaled) to a Recipe object */
  const resolveRecipe = useCallback((id: string): Recipe | undefined => {
    if (id.startsWith("saved_")) {
      const { baseId, scale } = parseScaledRecipeId(id);
      const savedId = baseId.replace("saved_", "");
      const saved = allSavedRecipes.find(r => r.id === savedId);
      if (!saved) return undefined;
      const totals = getRecipeTotals(saved);
      const base: Recipe = {
        id: baseId,
        label: saved.name,
        emoji: saved.icon || "📋",
        cal: Math.round(totals.kcal * scale),
        protein: Math.round(totals.protein * scale),
        ingredients: saved.recipe_ingredients.map(ing => {
          const factor = (ing.quantity_grams / 100) * scale;
          const food = ing.taco_foods;
          return {
            name: food.description,
            amount: `${Math.round(ing.quantity_grams * scale)}g`,
            cal: Math.round((food.energy_kcal || 0) * factor),
            protein: Math.round((food.protein_g || 0) * factor * 10) / 10,
            carbs: Math.round((food.carbohydrate_g || 0) * factor * 10) / 10,
            fat: Math.round((food.lipid_g || 0) * factor * 10) / 10,
          };
        }),
      };
      return { ...base, id };
    }
    return findRecipeById(id);
  }, [allSavedRecipes]);

  /** Get all options for the current meal type, each as an array of resolved Recipe[] */
  const dietOptionsForMeal = useMemo(() => {
    const mealKeys = diaryMealToRecipeKey[formData.mealType] || [];
    // Combine options from all relevant meal keys
    // For most types there's 1 key; for "snack" there are 2 (snack + optional)
    // We take the max number of options and merge them per-index
    let maxOpts = 0;
    const optionsByKey: Record<string, string[][]> = {};
    for (const key of mealKeys) {
      const opts = mealsForDate[key] || [[]];
      optionsByKey[key] = opts;
      maxOpts = Math.max(maxOpts, opts.length);
    }
    if (maxOpts === 0) return [];

    const result: Recipe[][] = [];
    for (let i = 0; i < maxOpts; i++) {
      const recipesForOption: Recipe[] = [];
      const seen = new Set<string>();
      for (const key of mealKeys) {
        const opts = optionsByKey[key] || [[]];
        const optionIds = opts[Math.min(i, opts.length - 1)] || [];
        for (const id of optionIds) {
          if (seen.has(id)) continue;
          seen.add(id);
          const recipe = resolveRecipe(id);
          if (recipe) recipesForOption.push(recipe);
        }
      }
      if (recipesForOption.length > 0) result.push(recipesForOption);
    }
    return result;
  }, [formData.mealType, mealsForDate, resolveRecipe]);

  // When meal type or options change, reset to first option with no selection
  useEffect(() => {
    setSelectedDietOption(0);
    setSelectedRecipes([]);
  }, [formData.mealType, dietOptionsForMeal.length]);

  const currentOptionRecipes = dietOptionsForMeal[selectedDietOption] || [];

  const registeredDescriptions = useMemo(() => {
    const set = new Set<string>();
    dayEntries
      .filter(e => e.meal_type === formData.mealType)
      .forEach(e => set.add(e.description));
    return set;
  }, [dayEntries, formData.mealType]);

  // Plan gating
  const formRef = useRef<HTMLDivElement>(null);

  if (planLoading || loading) {
    return <DiarySkeleton />;
  }

  if (!selectedPlan || selectedPlan === "basic" || selectedPlan === "free") {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
          <Lock className="w-8 h-8 text-muted-foreground" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">Diário Alimentar</h2>
        <p className="text-muted-foreground mb-6 max-w-sm">
          O diário alimentar está disponível a partir do <strong>Plano Plus</strong>. Registre suas refeições com fotos e acompanhe sua alimentação diária.
        </p>
        <Button onClick={() => navigate("/plans")} className="gradient-primary text-primary-foreground">
          Ver Planos
        </Button>
      </motion.div>
    );
  }

  const toggleRecipeSelection = (recipeId: string) => {
    setSelectedRecipes(prev =>
      prev.includes(recipeId) ? prev.filter(r => r !== recipeId) : [...prev, recipeId]
    );
  };

  const resetForm = () => {
    setShowForm(false);
    setSelectedRecipes([]);
    setPhotoFile(null);
    setPhotoPreview("");
    setFormData({
      mealType: "breakfast", description: "", calories: "", protein: "", carbs: "", fat: "",
      time: new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
    });
  };

  const createEntry = async (entryData: any) => {
    setSaving(true);
    try {
      if (photoFile) {
        const { compressImage } = await import("@/lib/imageCompressor");
        const compressed = await compressImage(photoFile);
        const fd = new FormData();
        fd.append("data", JSON.stringify(entryData));
        fd.append("photo", compressed);
        await apiFetch("api-diary", { action: "create" }, { method: "POST", formData: fd });
      } else {
        await apiFetch("api-diary", { action: "create" }, { method: "POST", body: entryData });
      }
      toast({ title: "Refeição registrada! 🎉", description: "Seu registro foi salvo com sucesso." });
      await loadEntries();
      loadAchievementsContext();
      resetForm();
    } catch (e: any) {
      console.error("Failed to create diary entry:", e);
      toast({ title: "Erro ao registrar", description: e.message || "Tente novamente", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleAddFromDiet = () => {
    if (selectedRecipes.length === 0) return;
    const items = selectedRecipes.map(recipeId => {
      const recipe = resolveRecipe(recipeId);
      if (!recipe) return null;
      const totalCarbs = recipe.ingredients.reduce((s, i) => s + i.carbs, 0);
      const totalFat = recipe.ingredients.reduce((s, i) => s + i.fat, 0);
      return {
        date: selectedDateStr,
        meal_type: formData.mealType,
        description: recipe.label,
        calories: recipe.cal,
        protein: recipe.protein,
        carbs: Math.round(totalCarbs),
        fat: Math.round(totalFat),
        time: formData.time,
        from_diet: true,
      };
    }).filter(Boolean);
    if (items.length > 0) createEntry({ items });
  };

  const handleAddCustom = () => {
    if (!formData.description) return;
    createEntry({
      date: selectedDateStr,
      meal_type: formData.mealType,
      description: formData.description,
      calories: Number(formData.calories) || 0,
      protein: Number(formData.protein) || 0,
      carbs: Number(formData.carbs) || 0,
      fat: Number(formData.fat) || 0,
      time: formData.time,
      from_diet: false,
    });
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await apiFetch("api-diary", { action: "delete", id }, { method: "DELETE" });
      toast({ title: "Registro excluído", description: "A refeição foi removida do diário." });
      await loadEntries();
      loadAchievementsContext();
    } catch (e: any) {
      console.error("Failed to delete diary entry:", e);
      toast({ title: "Erro ao excluir", description: e.message || "Tente novamente", variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview("");
  };

  const handleDayClick = (day: number) => {
    setSelectedDate(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day));
  };

  const prevMonth = () => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1));
  const nextMonth = () => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1));

  const isToday = selectedDateStr === todayStr;
  const dayLabel = isToday ? "Hoje" : selectedDate.toLocaleDateString("pt-BR", { day: "2-digit", month: "long" });


  const scrollToForm = () => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        const el = formRef.current;
        if (!el) return;
        const headerOffset = 64; // mobile sticky header (~h-14)
        const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top, behavior: "smooth" });
      }, 50);
    });
  };

  const openForm = () => {
    setShowForm(true);
    setFormMode("diet");
    setSelectedRecipes([]);
    scrollToForm();
  };


  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-col items-center text-center sm:flex-row sm:text-left sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Diário Alimentar</h1>
          <p className="text-muted-foreground">Registre suas refeições e acompanhe seu progresso</p>
        </div>
        <div className="flex gap-2">
          <div className="flex items-center gap-1.5 px-3 py-2 bg-card border border-border rounded-full text-sm">
            <Timer className="w-4 h-4 text-primary" />
            <span className="font-semibold text-foreground">{streak}</span>
            <span className="text-muted-foreground">dias</span>
          </div>
          <AchievementsSheet precomputedContext={achievementsContext || undefined}>
            <button className="flex items-center gap-1.5 px-3 py-2 bg-card border border-border rounded-full text-sm hover:border-primary/30 transition-colors">
              <Award className="w-4 h-4 text-primary" />
              <span className="text-muted-foreground">Conquistas</span>
              <span className="font-semibold text-primary-foreground bg-primary px-2 py-0.5 rounded-full text-xs">{unlockedAchievements}/{TOTAL_ACHIEVEMENTS}</span>
            </button>
          </AchievementsSheet>
        </div>
      </div>

      {/* Main layout */}
      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        {/* Calendar */}
        <div className="bg-card rounded-2xl p-5 shadow-card border border-border self-start">
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="p-1 hover:bg-muted rounded-lg transition-colors">
              <ChevronLeft className="w-4 h-4 text-muted-foreground" />
            </button>
            <span className="text-sm font-semibold text-foreground capitalize">{monthName}</span>
            <button onClick={nextMonth} className="p-1 hover:bg-muted rounded-lg transition-colors">
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center">
            {weekDays.map(d => (
              <div key={d} className="text-xs font-medium text-muted-foreground py-1">{d}</div>
            ))}
            {Array.from({ length: firstDayOfMonth }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dayStr = dateToString(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day));
              const isSelected = dayStr === selectedDateStr;
              const hasEntry = datesWithEntries.has(dayStr);
              const isOnTarget = datesOnTarget.has(dayStr);
              const isTodayDay = dayStr === todayStr;
              const isFuture = dayStr > todayStr;
              return (
                <button
                  key={day}
                  onClick={() => !isFuture && handleDayClick(day)}
                  disabled={isFuture}
                  className={`relative w-8 h-8 mx-auto rounded-full text-sm font-medium transition-all ${
                    isFuture
                      ? "text-muted-foreground/30 cursor-not-allowed"
                      : isSelected
                      ? "bg-primary text-primary-foreground"
                      : isTodayDay
                      ? "text-primary font-bold"
                      : "text-foreground hover:bg-muted"
                  }`}
                >
                  {day}
                  {hasEntry && !isSelected && !isFuture && (
                    <span className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${isOnTarget ? "bg-primary" : "bg-primary/40"}`} />
                  )}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-4 pt-3 border-t border-border text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-primary/40" />
              Registrado
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-primary" />
              Meta atingida
            </div>
          </div>
        </div>

        {/* Right content */}
        <div className="space-y-4">
          {/* Day header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg">📅</span>
              <div>
                <h2 className="text-lg font-bold text-foreground">{dayLabel}</h2>
                <p className="text-sm text-muted-foreground">{dayEntries.length} refeições registradas</p>
              </div>
            </div>
            <Button onClick={openForm} className="gradient-primary text-primary-foreground" size="default">
              <Plus className="w-4 h-4 mr-1.5" />
              Registrar Refeição
            </Button>
          </div>

          {/* Calories card */}
          <div className="bg-card rounded-2xl p-5 shadow-card border border-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Timer className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Calorias Consumidas</p>
                <p className="text-2xl font-bold text-foreground">{dayCalories} kcal</p>
              </div>
            </div>
            <div className="relative mb-3">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                <span>{dayCalories}</span>
                <span className="flex items-center gap-1 text-primary">
                  <span className="w-2 h-2 rounded-full bg-primary" />
                  {targetCalories} kcal
                </span>
              </div>
              <div className="w-full h-3 bg-primary/10 rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full gradient-primary"
                  initial={{ width: 0 }}
                  animate={{ width: `${caloriePercent}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground bg-primary/5 rounded-xl py-2.5">
              <TrendingDown className="w-4 h-4 text-primary" />
              <span>{remainingCalories > 0 ? `${remainingCalories} kcal restantes` : "Meta atingida! 🎉"}</span>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="bg-muted/50 rounded-xl p-3 text-center">
                <p className="text-xs text-primary font-medium mb-1">Proteína</p>
                <p className="text-lg font-bold text-foreground">{dayProtein}g</p>
              </div>
              <div className="bg-muted/50 rounded-xl p-3 text-center">
                <p className="text-xs text-primary font-medium mb-1">Carboidratos</p>
                <p className="text-lg font-bold text-foreground">{dayCarbs}g</p>
              </div>
              <div className="bg-muted/50 rounded-xl p-3 text-center">
                <p className="text-xs text-primary font-medium mb-1">Gorduras</p>
                <p className="text-lg font-bold text-foreground">{dayFat}g</p>
              </div>
            </div>
          </div>

          {/* Water Tracker */}
          {(() => {
            const weight = dietPlan?.measures?.weight || measures?.weight || 0;
            const goal = dietPlan?.measures?.goal || measures?.goal || "maintain";
            const waterMultiplier = goal === "gain" ? 0.045 : goal === "lose" ? 0.04 : 0.035;
            const waterTargetMl = weight ? Math.round(Math.min(4000, Math.max(2000, weight * waterMultiplier * 1000))) : 2000;
            return (
              <WaterTracker
                date={selectedDateStr}
                targetMl={waterTargetMl}
                onUpdate={loadAchievementsContext}
              />
            );
          })()}

          {/* Registration form */}
          {showForm && (
            <motion.div ref={formRef} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-card rounded-2xl p-5 shadow-card border border-primary/30 space-y-4 scroll-mt-20">

              <h3 className="font-semibold text-foreground">Registrar Refeição</h3>

              {/* Meal type selector */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(mealTypeLabels).map(([key, { label, emoji }]) => (
                  <button
                    key={key}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, mealType: key }));
                      setSelectedRecipes([]);
                    }}
                    className={`flex items-center gap-2 p-2.5 rounded-xl border-2 transition-all text-sm ${
                      formData.mealType === key
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/30"
                    }`}
                  >
                    <span>{emoji}</span>
                    <span className="font-medium text-foreground text-xs">{label}</span>
                  </button>
                ))}
              </div>

              {/* Mode toggle */}
              <div className="flex rounded-xl bg-muted p-1 gap-1">
                <button
                  onClick={() => setFormMode("diet")}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all ${
                    formMode === "diet" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Check className="w-3.5 h-3.5" />
                  Da minha dieta
                </button>
                <button
                  onClick={() => setFormMode("custom")}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all ${
                    formMode === "custom" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <PlusCircle className="w-3.5 h-3.5" />
                  Item avulso
                </button>
              </div>

              {/* Diet items mode */}
              {formMode === "diet" && (
                <div className="space-y-3">
                  {dietOptionsForMeal.length === 0 ? (
                    <div className="text-center py-6 bg-muted/30 rounded-xl">
                      <p className="text-sm text-muted-foreground">Nenhum item da dieta para este período</p>
                      <p className="text-xs text-muted-foreground/70 mt-1">Monte sua dieta primeiro ou adicione um item avulso</p>
                      <div className="flex gap-2 justify-center mt-3">
                        <Button onClick={() => navigate("/diet-builder")} variant="outline" size="sm">Montar Dieta</Button>
                        <Button onClick={() => setFormMode("custom")} variant="outline" size="sm">Item Avulso</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Option selector */}
                      {dietOptionsForMeal.length > 1 && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-2">Escolha uma opção da sua dieta:</p>
                          <div className="flex gap-1.5 flex-wrap">
                            {dietOptionsForMeal.map((_, idx) => (
                              <button
                                key={idx}
                                onClick={() => {
                                  setSelectedDietOption(idx);
                                  setSelectedRecipes([]);
                                }}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                  selectedDietOption === idx
                                    ? "gradient-primary text-primary-foreground shadow-sm"
                                    : "bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80"
                                }`}
                              >
                                Opção {idx + 1}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      <p className="text-xs text-muted-foreground">Selecione o que você consumiu:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {currentOptionRecipes.map(recipe => {
                          const isSelected = selectedRecipes.includes(recipe.id);
                          const totalCarbs = recipe.ingredients.reduce((s, i) => s + i.carbs, 0);
                          const totalFat = recipe.ingredients.reduce((s, i) => s + i.fat, 0);
                          return (
                            <button
                              key={recipe.id}
                              onClick={() => toggleRecipeSelection(recipe.id)}
                              className={`relative flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${
                                isSelected ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-primary/30"
                              }`}
                            >
                              {isSelected && (
                                <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full gradient-primary flex items-center justify-center">
                                  <Check className="w-3 h-3 text-primary-foreground" />
                                </div>
                              )}
                              <RecipeEmoji emoji={recipe.emoji} />
                              <span className="text-xs font-medium text-foreground">{recipe.label}</span>
                              <div className="flex flex-wrap justify-center gap-1.5 text-[10px] text-muted-foreground">
                                <span className="text-primary font-medium">{recipe.cal} kcal</span>
                                <span>{recipe.protein}g P</span>
                                <span>{Math.round(totalCarbs)}g C</span>
                                <span>{Math.round(totalFat)}g G</span>
                              </div>
                              {registeredDescriptions.has(recipe.label) && (
                                <span className="text-[10px] text-primary/80 font-medium flex items-center gap-0.5">
                                  <Check className="w-2.5 h-2.5" /> Já registrado
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {/* Time + photo for diet mode */}
                  {currentOptionRecipes.length > 0 && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">Horário</label>
                        <input type="time" value={formData.time} onChange={e => setFormData(prev => ({ ...prev, time: e.target.value }))} className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">Foto (opcional)</label>
                        {photoPreview ? (
                          <div className="relative w-11 h-11 rounded-lg overflow-hidden border border-border">
                            <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                            <button onClick={clearPhoto} className="absolute top-0 right-0 w-4 h-4 bg-destructive rounded-bl-lg flex items-center justify-center">
                              <Trash2 className="w-2.5 h-2.5 text-destructive-foreground" />
                            </button>
                          </div>
                        ) : (
                          <label className="flex items-center gap-2 px-3 py-2.5 bg-muted border border-border rounded-xl cursor-pointer hover:border-primary/30 transition-colors text-sm text-muted-foreground">
                            <Camera className="w-4 h-4" />
                            Foto
                            <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                          </label>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Custom item mode */}
              {formMode === "custom" && (
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">O que você comeu?</label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Ex: Arroz, feijão e frango grelhado"
                      className="w-full bg-muted border border-border rounded-xl px-4 py-3 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="text-sm text-muted-foreground mb-1 block">Calorias</label>
                      <input type="number" value={formData.calories} onChange={e => setFormData(prev => ({ ...prev, calories: e.target.value }))} placeholder="350" className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-foreground text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground mb-1 block">Proteína (g)</label>
                      <input type="number" value={formData.protein} onChange={e => setFormData(prev => ({ ...prev, protein: e.target.value }))} placeholder="25" className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-foreground text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground mb-1 block">Carbos (g)</label>
                      <input type="number" value={formData.carbs} onChange={e => setFormData(prev => ({ ...prev, carbs: e.target.value }))} placeholder="40" className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-foreground text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground mb-1 block">Gordura (g)</label>
                      <input type="number" value={formData.fat} onChange={e => setFormData(prev => ({ ...prev, fat: e.target.value }))} placeholder="12" className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-foreground text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm text-muted-foreground mb-1 block">Horário</label>
                      <input type="time" value={formData.time} onChange={e => setFormData(prev => ({ ...prev, time: e.target.value }))} className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground mb-1 block">Foto (opcional)</label>
                      {photoPreview ? (
                        <div className="relative w-11 h-11 rounded-lg overflow-hidden border border-border">
                          <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                          <button onClick={clearPhoto} className="absolute top-0 right-0 w-4 h-4 bg-destructive rounded-bl-lg flex items-center justify-center">
                            <Trash2 className="w-2.5 h-2.5 text-destructive-foreground" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-2 px-3 py-2.5 bg-muted border border-border rounded-xl cursor-pointer hover:border-primary/30 transition-colors text-sm text-muted-foreground">
                          <Camera className="w-4 h-4" />
                          Foto
                          <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={formMode === "diet" ? handleAddFromDiet : handleAddCustom}
                  className="flex-1 gradient-primary text-primary-foreground"
                  disabled={saving || (formMode === "diet" ? selectedRecipes.length === 0 : !formData.description)}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                      Salvando...
                    </>
                  ) : formMode === "diet"
                    ? `Registrar ${selectedRecipes.length} ${selectedRecipes.length === 1 ? "item" : "itens"}`
                    : "Salvar Registro"}
                </Button>
                <Button onClick={resetForm} variant="outline" disabled={saving}>
                  Cancelar
                </Button>
              </div>
            </motion.div>
          )}

          {/* Meals of the day */}
          <div className="bg-card rounded-2xl p-5 shadow-card border border-border">
            <h3 className="font-semibold text-foreground mb-4">Refeições do Dia</h3>
            {dayEntries.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-3">
                  <UtensilsCrossed className="w-7 h-7 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground">Nenhuma refeição registrada</p>
                <p className="text-sm text-muted-foreground/70 mt-1">Clique no botão acima para adicionar</p>
              </div>
            ) : (
              <div className="space-y-3">
                {dayEntries.map(entry => {
                  const ml = mealTypeLabels[entry.meal_type] || mealTypeLabels.other;
                  const isDeleting = deletingId === entry.id;
                  return (
                    <motion.div
                      key={entry.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex gap-3 p-3 rounded-xl bg-muted/30 border border-border"
                    >
                      {entry.photo_url && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 border border-border cursor-pointer hover:opacity-80 transition-opacity">
                              <img src={entry.photo_url} alt="Refeição" className="w-full h-full object-cover" />
                            </div>
                          </DialogTrigger>
                          <DialogContent className="max-w-[85vw] sm:max-w-sm w-auto p-3 rounded-2xl bg-background/95 backdrop-blur-sm">
                            <DialogTitle className="sr-only">Foto da refeição</DialogTitle>
                            <img src={entry.photo_url} alt="Refeição" className="max-h-[50vh] sm:max-h-[60vh] w-auto rounded-xl object-contain" />
                          </DialogContent>
                        </Dialog>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <RecipeEmoji emoji={ml.emoji} className="text-sm" />
                          <span className="text-xs font-medium text-primary">{ml.label}</span>
                          {entry.from_diet && (
                            <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium">da dieta</span>
                          )}
                          <span className="text-xs text-muted-foreground ml-auto flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {entry.time}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-foreground truncate">{entry.description}</p>
                        <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                          {Number(entry.calories) > 0 && <span className="text-primary font-medium">{entry.calories} kcal</span>}
                          {Number(entry.protein) > 0 && <span>{entry.protein}g P</span>}
                          {Number(entry.carbs) > 0 && <span>{entry.carbs}g C</span>}
                          {Number(entry.fat) > 0 && <span>{entry.fat}g G</span>}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDelete(entry.id)}
                        disabled={isDeleting}
                        className="text-muted-foreground hover:text-destructive transition-colors self-start mt-1 disabled:opacity-50"
                      >
                        {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </button>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Diary;
